/** biome-ignore-all lint/suspicious/noConsole: telemetry layer needs console diagnostics */
import { PostHog } from '@cobliteam/analytics';
import { UserRole } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useEffect, useRef, useState } from 'react';
import { markHandled, wasHandled } from './dedup';
import SurveyDialog from './SurveyDialog';
import { surveyEventBus } from './surveyEventBus';
import type { Survey, SurveyResponses, SurveyTriggerEventDetail } from './types';

interface ActiveSurvey {
  survey: Survey;
  context?: Record<string, unknown>;
  persistDismissal: boolean;
}

const matches = (survey: Survey, key: string) => survey.name === key || survey.linked_flag_key === key;

const SurveyHost = () => {
  const { posthogEnabled } = useFeatureFlags();
  const { user } = useUser();
  const isCobler = user?.role === UserRole.COBLER;
  const [active, setActive] = useState<ActiveSurvey | null>(null);
  const activeRef = useRef<ActiveSurvey | null>(null);
  const sessionSeen = useRef<Set<string>>(new Set());
  const inFlightKeys = useRef<Set<string>>(new Set());

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    const handle = async ({ key, context, persistDismissal, resolve }: SurveyTriggerEventDetail) => {
      if (activeRef.current) {
        resolve(false);
        return;
      }
      if (!posthogEnabled) {
        resolve(false);
        return;
      }
      // Block on any in-flight key, not just the same one — two different
      // triggers firing in the same microtask would otherwise both pass the
      // activeRef guard and duplicate the `survey shown` capture.
      if (inFlightKeys.current.size > 0) {
        resolve(false);
        return;
      }
      inFlightKeys.current.add(key);
      try {
        // Cobler bypass: skip dedup + targeting filters so internal testers
        // can preview the live survey repeatedly without polluting analytics.
        const surveys = isCobler ? await PostHog.getAllSurveys(true) : await PostHog.getActiveSurveys(true);
        const match = surveys.find((s) => matches(s, key));
        if (!match) {
          resolve(false);
          return;
        }
        if (!isCobler && (sessionSeen.current.has(match.id) || wasHandled(user?.id, match.id))) {
          resolve(false);
          return;
        }
        sessionSeen.current.add(match.id);
        if (!isCobler) {
          PostHog.capture('survey shown', { $survey_id: match.id, $survey_name: match.name, ...context });
        }
        setActive({ survey: match, context, persistDismissal });
        resolve(true);
      } catch (error) {
        console.error('[SurveyHost] failed to match survey', error);
        resolve(false);
      } finally {
        inFlightKeys.current.delete(key);
      }
    };

    const unsubscribe = surveyEventBus.subscribe(handle);
    return unsubscribe;
  }, [posthogEnabled, isCobler, user?.id]);

  if (!active) return null;

  const handleSubmit = (responses: SurveyResponses) => {
    if (isCobler) {
      // Reset per-active selection without polluting PostHog person properties
      // (which would prevent re-showing). Also skip localStorage dedup.
      sessionSeen.current.delete(active.survey.id);
      setActive(null);
      return;
    }
    const props: Record<string, unknown> = {
      $survey_id: active.survey.id,
      $survey_name: active.survey.name,
      ...active.context,
    };
    for (const [idx, response] of Object.entries(responses)) {
      const key = Number(idx) === 0 ? '$survey_response' : `$survey_response_${idx}`;
      props[key] = response;
    }
    PostHog.capture('survey sent', props);
    markHandled(user?.id, active.survey.id, 'answered');
  };

  const handleDismiss = () => {
    const id = active.survey.id;
    if (isCobler) {
      sessionSeen.current.delete(id);
      setActive(null);
      return;
    }
    if (!wasHandled(user?.id, id)) {
      PostHog.capture('survey dismissed', { $survey_id: id, $survey_name: active.survey.name, ...active.context });
      if (active.persistDismissal) {
        markHandled(user?.id, id, 'dismissed');
      }
    }
    if (!active.persistDismissal) {
      // Allow the same trigger to re-prompt later in the same session.
      sessionSeen.current.delete(id);
    }
    setActive(null);
  };

  return <SurveyDialog survey={active.survey} onSubmit={handleSubmit} onDismiss={handleDismiss} />;
};

export default SurveyHost;
